import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  Typography,
} from "@mui/material";
import { memo, useEffect, useLayoutEffect, useRef, useState } from "react";
import GoogleMapComponent from "../map/GoogleMapComponent";
import { LocationOn, Place } from "@mui/icons-material";
import { IoMdLocate } from "react-icons/io";
import { MdKeyboardArrowDown, MdKeyboardArrowRight } from "react-icons/md";
import { stationFilterData } from "../../data/svg/station_filter_data";
import eko from "../../assets/img/eko.png";
import sprint from "../../assets/img/sprint.png";
import petroCanada from "../../assets/img/petro-canada.png";
import shell_logo from "../../assets/img/shell-logo.jpg";
import ultramar_logo from "../../assets/img/ultramar-logo.jpg";
import { useDispatch, useSelector } from "react-redux";
import {
  calculateDistance,
  calculateDistanceRaw,
} from "../../helper/distanceCalc";
import { ImLocation } from "react-icons/im";
import { setStationList } from "../../redux/features/stations/stationsSlice";
import { Autocomplete, useJsApiLoader } from "@react-google-maps/api";
const isDev = process.env.REACT_APP_TYPE === "development";

const FilterAndMap = memo(() => {
  const inputRef = useRef(null);
  const [locationSearch, setLocationSearch] = useState("");
  const { isLoaded } = useJsApiLoader({
    id: "google-map-script-search",
    googleMapsApiKey: isDev
      ? process.env.REACT_APP_GOOGLE_API_KEY
      : window?.ekoSettings?.googleMapApiKey,
    libraries: ["places", "geometry", "drawing"],
  });
  const dispatch = useDispatch();
  const stations = useSelector((state) => state.stations.stattionList);
  const allStatationList = useSelector(
    (state) => state.stations.allStatationList
  );
  const [geolocation, setGeolocation] = useState({
    latitude: 0,
    longitude: 0,
    state: null,
  });
  const [navigate, setNavigate] = useState(null);
  const [advancedFilterOpen, setAdvancedFilterOpen] = useState(false);
  const [activeFilterOption, setActiveFilterOption] = useState([]);
  const handleActiveFilterOption = (active) => {
    if (activeFilterOption.includes(active)) {
      setActiveFilterOption(
        activeFilterOption.filter((item) => item !== active)
      );
    } else {
      setActiveFilterOption([...activeFilterOption, active]);
    }
  };

  const getGeoLocation = async () => {
    const permissionStatus = await navigator.permissions.query({
      name: "geolocation",
    });
    if (permissionStatus.state === "denied") {
      console.log("Geolocation permission denied");
      return;
    }
    if (permissionStatus.state === "prompt") {
      console.log("Geolocation permission prompt");
      setGeolocation((prev) => ({ ...prev, state: "prompt" }));
    } else if (permissionStatus.state === "granted") {
      console.log("Geolocation permission granted");
      setGeolocation((prev) => ({ ...prev, state: "granted" }));
    }
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition((position) => {
        setGeolocation((prev) => {
          return {
            ...prev,
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          };
        });
      });
    } else {
      console.log("Geolocation is not available");
    }
  };
  useLayoutEffect(() => {
    getGeoLocation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filterStattionHandler = () => {
    let filtered = [...allStatationList];

    if (activeFilterOption?.length > 0) {
      filtered = allStatationList.filter((item) =>
        activeFilterOption.every((selected) => item?.customFields?.[selected])
      );
    }

    // Sort
    filtered = filtered.sort((a, b) => {
      const distanceA = calculateDistanceRaw(
        a.latitude,
        a.longitude,
        geolocation.latitude,
        geolocation.longitude
      );
      const distanceB = calculateDistanceRaw(
        b.latitude,
        b.longitude,
        geolocation.latitude,
        geolocation.longitude
      );

      return distanceA - distanceB;
    });

    dispatch(setStationList(filtered));
  };

  useEffect(() => {
    filterStattionHandler();
    // eslint-disable-next-line
  }, [activeFilterOption]);

  useEffect(() => {
    // localtionbase filter accending order
    if (geolocation?.latitude && geolocation?.longitude) {
      const sortedStations = [...stations].sort((a, b) => {
        const distanceA = calculateDistanceRaw(
          a.latitude,
          a.longitude,
          geolocation.latitude,
          geolocation.longitude
        );
        const distanceB = calculateDistanceRaw(
          b.latitude,
          b.longitude,
          geolocation.latitude,
          geolocation.longitude
        );

        return distanceA - distanceB;
      });
      dispatch(setStationList(sortedStations));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geolocation]);

  return (
    <Box>
      <Box
        sx={{
          boxShadow: "0 14px 8px -8px rgba(0,0,0,0.09)",
          padding: "35px 20px",
          boxSizing: "border-box",
          backgroundColor: "var(--white)",
          zIndex: 1,
          position: "relative", // Make
        }}
      >
        <Grid container alignItems={"center"} columnSpacing={2}>
          <Grid
            item
            xs={12}
            lg={2}
            sx={{
              mt: {
                xs: 2, // mt-2 (theme.spacing(2) = 16px)
                lg: 0, // mt-0
              },
              ml: {
                md: "20%",
                sm: "10%",
                xs: "3%",
                lg: "0px",
              },
              mr: {
                md: "20%",
                sm: "10%",
                xs: "3%",
                lg: "0px",
              },
            }}
          >
            <FormControl
              fullWidth
              sx={{
                "& .MuiOutlinedInput-root": {
                  padding: "0px 5px",
                  borderRadius: "20px",
                  "& fieldset": {
                    border: "2px solid var(--grey-3)",
                  },
                  "&:hover fieldset": {
                    border: "2px solid var(--grey-3)",
                  },
                  "&.Mui-focused fieldset": {
                    border: "2px solid var(--grey-3)",
                  },
                },
                "& .MuiInputLabel-root": {
                  color: "white",
                },
              }}
            >
              {isLoaded ? (
                <Autocomplete
                  onPlaceChanged={() => {
                    const place = inputRef.current.getPlace(); // Get the selected place details
                    if (place.geometry && place.geometry.location) {
                      // Extract latitude and longitude
                      const lat = place.geometry.location.lat();
                      const lng = place.geometry.location.lng();
                      setGeolocation({
                        latitude: lat,
                        longitude: lng,
                        state: "granted",
                      });
                      console.log("Selected Place:", lat, lng);
                      setLocationSearch(place.formatted_address);
                    }
                  }}
                  onLoad={(ref) => (inputRef.current = ref)}
                  options={{
                    fields: [
                      "geometry",
                      "address_components",
                      "formatted_address",
                    ],
                    componentRestrictions: { country: "CA" },
                  }}
                >
                  <input
                    type="text"
                    placeholder="Trouver un Sprint"
                    onChange={(e) => {
                      setLocationSearch(e.target.value);
                    }}
                    style={{
                      width: "100%",
                      height: "40px",
                      padding: "10px 15px",
                      fontSize: "16px",
                      border: "2px solid var(--primary)",
                      borderRadius: "20px",
                      outline: "none",
                    }}
                  />
                </Autocomplete>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100%",
                  }}
                >
                  <CircularProgress />
                </Box>
              )}
              <LocationOn
                sx={{
                  position: "absolute",
                  right: "15px",
                  top: "8px",
                  color: locationSearch ? "var(--primary)" : "var(--grey-2)",
                }}
              />
            </FormControl>
          </Grid>
          <Grid
            item
            md={0.2}
            sx={{
              color: "var(--gray-1)",
              fontSize: "14px",
              display: { xs: "none", lg: "block" },
            }}
          >
            ou
          </Grid>
          <Grid
            sx={{
              mt: {
                xs: 2, // mt-2 (theme.spacing(2) = 16px)
                lg: 0, // mt-0
              },
              ml: {
                md: "20%",
                sm: "10%",
                xs: "3%",
                lg: "0px",
              },
              mr: {
                md: "20%",
                sm: "10%",
                xs: "3%",
                lg: "0px",
              },
            }}
            item
            xs={12}
            lg={2}
          >
            <Button
              disabled={geolocation.state === "denied"}
              disableRipple
              fullWidth
              variant="contained"
              onClick={() => getGeoLocation()}
              sx={{
                justifyContent: "space-between",
                backgroundColor: "transparent",
                color: "var(--primary)",
                borderRadius: "20px",
                border: "2px solid var(--primary)",
                boxShadow: "none",
                transition: "all 0.25s",
                transitionProperty: "background-color, color, transform",
                ":hover": {
                  backgroundColor: "var(--primary)",
                  color: "var(--white)",
                  transform: "scale(1.05)",
                  boxShadow: "none",
                },
              }}
              endIcon={<IoMdLocate />}
            >
              Localiser
            </Button>
          </Grid>
          <Grid item xs={5.7}></Grid>
          <Grid
            item
            xs={12}
            lg={2}
            className="flex items-center lg:justify-end justify-center cursor-pointer"
            sx={{
              mt: {
                xs: 2, // mt-2 (theme.spacing(2) = 16px)
                lg: 0, // mt-0
              },
            }}
          >
            <Grid
              key="reset"
              sx={{
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                transition: "transform .25s",
                transform: "scale(1)",
                transformOrigin: "left center",
                ":hover": {
                  transform: "scale(1.05)",
                },
              }}
            >
              {activeFilterOption?.length > 0 && (
                <Box
                  onClick={() => {
                    setActiveFilterOption([]);
                  }}
                  className="lg:w-[150px] w-[0px] lg:block hidden"
                  sx={{
                    fontSize: "13px",
                    width: "150px",
                    color: "var(--primary)",
                    textTransform: "uppercase",
                    fontWeight: "bold",
                    paddingBottom: "3px",
                  }}
                >
                  Réinitialiser
                </Box>
              )}

              <Box
                className="lg:w-[150px] flex cursor-pointer center items-center justify-center"
                onClick={() => setAdvancedFilterOpen((prev) => !prev)}
                sx={{
                  mt: {
                    xs: 2, // mt-2 (theme.spacing(2) = 16px)
                    lg: 0, // mt-0
                  },
                }}
              >
                <Typography
                  className={` ${
                    advancedFilterOpen
                      ? "!text-[var(--primary)]"
                      : "!text-[var(--grey-2)]"
                  }  !text-[13px] !uppercase !font-bold`}
                  variant="body2"
                  component={"span"}
                  sx={{
                    mt: {
                      xs: 0,
                    },
                  }}
                >
                  Filters
                  {activeFilterOption?.length > 0 &&
                    `(${activeFilterOption?.length})`}
                </Typography>
                <Typography
                  className={`${
                    advancedFilterOpen
                      ? "!text-[var(--primary)]"
                      : "!text-[var(--grey-2)]"
                  }  !text-[18px] !font-bold`}
                  variant="body2"
                  component={"span"}
                >
                  {advancedFilterOpen ? (
                    <MdKeyboardArrowDown />
                  ) : (
                    <MdKeyboardArrowRight />
                  )}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Grid>
        {advancedFilterOpen && (
          <Box className="mt-6">
            <Grid container rowSpacing={2}>
              {stationFilterData?.map((item) => (
                <Grid
                  item
                  lg={3}
                  md={4}
                  sm={6}
                  xs={12}
                  key={item.id}
                  sx={{
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    transition: "transform .25s",
                    transform: "scale(1)",
                    transformOrigin: "left center",
                    ":hover": {
                      transform: "scale(1.05)",
                    },
                  }}
                  onClick={() => handleActiveFilterOption(item?.slug)}
                >
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Box
                      sx={{
                        width: "35px",
                        height: "35px",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        border: "3px solid var(--primary)",
                        borderRadius: "50%",
                        backgroundColor: activeFilterOption.includes(item?.slug)
                          ? "var(--primary)"
                          : "var(--white)",
                        color: activeFilterOption.includes(item?.slug)
                          ? "var(--white)"
                          : "var(--primary)",
                        transition: "all 0.3s",
                        transitionProperty: "fill, background-color",
                        "& svg": {
                          width: "22px",
                          height: "22px",
                        },
                      }}
                    >
                      {item.icon}
                    </Box>
                    <Box
                      sx={{
                        paddingLeft: "10px",
                        fontSize: "13px",
                        width: "200px",
                      }}
                    >
                      {item.name}
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </Box>
      {/* Filter and Map */}
      <Grid
        container
        direction={{ md: "row-reverse" }}
        sx={{
          height: {
            xs: "1000px",
            lg: "700px",
          },
          overflowY: "auto",
          position: "relative",
        }}
      >
        <Grid
          item
          xs={12}
          lg={9}
          sx={{
            height: {
              xs: "400px",
              lg: "700px",
            },
          }}
        >
          <GoogleMapComponent
            geolocation={geolocation}
            isLoaded={isLoaded}
            setNavigate={setNavigate}
            navigate={navigate}
          />
        </Grid>
        <Grid item xs={12} lg={3}>
          <Grid
            item
            lg={3}
            xs={12}
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: navigate ? "100%" : "0px",
              height: {
                xs: "1000px",
                lg: "700px",
              },
              overflowY: navigate ? "auto" : "hidden",
              backgroundColor: "white",
              boxShadow: navigate ? 3 : 0,
              transform: navigate ? "translateX(0)" : "translateX(-100%)",
              transition: "transform 0.4s ease, width 0.4s ease",
              zIndex: 1300,
            }}
          >
            <Box
              sx={{
                backgroundColor: "var(--white)",
              }}
              className="p-5 pb-0"
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  marginTop: "10px",
                  marginBottom: "40px",
                }}
                onClick={() => setNavigate(null)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="30"
                  height="30"
                  viewBox="0 0 24 24"
                  style={{ cursor: "pointer", paddingRight: "10px" }}
                >
                  <path
                    fill="var(--grey-2)"
                    transform="matrix(-1 0 0 1 24 0)"
                    id="a"
                    d="M11.295 4.705a.998.998 0 0 0 0 1.41L16.17 11H5a1 1 0 0 0 0 2h11.17l-4.876 4.884a.998.998 0 0 0 1.411 1.411L20 12l-7.295-7.295a.997.997 0 0 0-1.41 0z"
                  ></path>
                </svg>

                <Typography
                  variant="body2"
                  component={"span"}
                  sx={{
                    color: "var(--grey-2)",
                    marginTop: "6px",
                    fontSize: "12px",
                    textTransform: "uppercase",
                    cursor: "pointer",
                  }}
                >
                  Retour
                </Typography>
              </Box>

              <Box className="flex gap-2">
                <Box
                  className="w-[50px] h-[50px]"
                  sx={{ boxShadow: "1px 1px 1px 0 rgba(10, 10, 10, 0.3)" }}
                >
                  <img className="w-full h-full" src={sprint} alt="sprint" />
                </Box>
                {navigate?.customFields?.banniere === "Petro-Canada" && (
                  <Box
                    className="w-[50px] h-[50px]"
                    sx={{ boxShadow: "1px 1px 1px 0 rgba(10, 10, 10, 0.3)" }}
                  >
                    <img
                      className="w-full h-full"
                      src={petroCanada}
                      alt="petroCanada"
                    />
                  </Box>
                )}

                {navigate?.customFields?.banniere === "Eko" && (
                  <Box
                    className="w-[50px] h-[50px]"
                    sx={{ boxShadow: "1px 1px 1px 0 rgba(10, 10, 10, 0.3)" }}
                  >
                    <img className="w-full h-full" src={eko} alt="eko" />
                  </Box>
                )}

                {navigate?.customFields?.banniere === "Ultramar" && (
                  <Box
                    className="w-[50px] h-[50px]"
                    sx={{ boxShadow: "1px 1px 1px 0 rgba(10, 10, 10, 0.3)" }}
                  >
                    <img
                      className="w-full h-full"
                      src={ultramar_logo}
                      alt="ultramar_logo"
                    />
                  </Box>
                )}
                {navigate?.customFields?.banniere === "Shell" && (
                  <Box
                    className="w-[50px] h-[50px]"
                    sx={{ boxShadow: "1px 1px 1px 0 rgba(10, 10, 10, 0.3)" }}
                  >
                    <img
                      className="w-full h-full"
                      src={shell_logo}
                      alt="shell_logo"
                    />
                  </Box>
                )}
              </Box>
              {geolocation?.latitude !== 0 && geolocation?.longitude !== 0 && (
                <Box
                  sx={{
                    color: "var(--grey-2)",
                    fontSize: "12px",
                    marginTop: "20px",
                    marginBottom: "20px",
                  }}
                >
                  {calculateDistance(
                    navigate?.latitude,
                    navigate?.longitude,
                    geolocation?.latitude,
                    geolocation?.longitude
                  )}
                </Box>
              )}
              <Box
                sx={{
                  color: "var(--grey-1)",
                  fontWeight: "bold",
                  fontSize: "22px",
                  padding: "5px 0px",
                }}
              >
                {navigate?.customFields?.raisonsociale}
              </Box>
              <Box
                sx={{
                  color: "var(--grey-2)",
                  fontSize: "14px",
                  fontWeight: 400,
                }}
              >
                <Typography variant="body2">
                  {navigate?.businessAddress?.line1}
                </Typography>
                <Typography variant="body2" sx={{ marginBottom: "20px" }}>
                  {navigate?.businessAddress?.city},{" "}
                  {navigate?.businessAddress?.postalCode}
                </Typography>
                <Typography variant="h6" sx={{ color: "var(--black)" }}>
                  <a href={navigate?.businessPhone}>
                    {navigate?.businessPhone}
                  </a>
                </Typography>
                <Box sx={{ marginTop: "20px", marginBottom: "20px" }}>
                  <Button
                    href={`https://www.google.com/maps/dir/?api=1&destination=${navigate?.latitude},${navigate?.longitude}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    disableRipple
                    variant="contained"
                    sx={{
                      justifyContent: "space-between",
                      backgroundColor: "transparent",
                      color: "var(--primary)",
                      borderRadius: "20px",
                      border: "2px solid var(--primary)",
                      boxShadow: "none",
                      transition: "all 0.25s",
                      transitionProperty: "background-color, color, transform",
                      ":hover": {
                        backgroundColor: "var(--primary)",
                        color: "var(--white)",
                        transform: "scale(1.05)",
                        boxShadow: "none",
                      },
                    }}
                    endIcon={<ImLocation />}
                  >
                    Directions
                  </Button>
                </Box>
                <Typography variant="h6" sx={{ color: "var(--black)" }}>
                  Horaire
                </Typography>
                <Box sx={{ marginTop: "20px", marginBottom: "20px" }}>
                  <Grid container rowSpacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2">Lundi</Typography>
                      <Typography variant="body2">Mardi</Typography>
                      <Typography variant="body2">Mercredi</Typography>
                      <Typography variant="body2">Jeudi</Typography>
                      <Typography variant="body2">Vendredi</Typography>
                      <Typography variant="body2">Samedi</Typography>
                      <Typography variant="body2">Dimanche</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2">{`${navigate?.customFields?.lundideb} - ${navigate?.customFields?.lundifin}`}</Typography>
                      <Typography variant="body2">{`${navigate?.customFields?.mardideb} - ${navigate?.customFields?.mardifin}`}</Typography>
                      <Typography variant="body2">{`${navigate?.customFields?.mercredideb} - ${navigate?.customFields?.mercredifin}`}</Typography>
                      <Typography variant="body2">{`${navigate?.customFields?.jeudideb} - ${navigate?.customFields?.jeudideb}`}</Typography>
                      <Typography variant="body2">{`${navigate?.customFields?.vendredideb} - ${navigate?.customFields?.vendredifin}`}</Typography>
                      <Typography variant="body2">{`${navigate?.customFields?.samedideb} - ${navigate?.customFields?.samedifin}`}</Typography>
                      <Typography variant="body2">{`${navigate?.customFields?.dimanchedeb} - ${navigate?.customFields?.dimanchefin}`}</Typography>
                    </Grid>
                  </Grid>
                </Box>
              </Box>
            </Box>
            <Box>
              <Grid
                container
                rowSpacing={2}
                sx={{ padding: "20px", paddingTop: 0 }}
              >
                {stationFilterData
                  ?.filter((i) => navigate?.customFields[i.slug])
                  ?.map((item) => (
                    <Grid
                      item
                      sm={6}
                      key={item.id}
                      sx={{
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        transition: "transform .25s",
                        transform: "scale(1)",
                        transformOrigin: "left center",
                        ":hover": {
                          transform: "scale(1.05)",
                        },
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Box
                          sx={{
                            width: "35px",
                            height: "35px",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            border: "3px solid var(--primary)",
                            borderRadius: "50%",
                            backgroundColor: "var(--white)",
                            color: "var(--primary)",
                            transition: "all 0.3s",
                            transitionProperty: "fill, background-color",
                            "& svg": {
                              width: "22px",
                              height: "22px",
                            },
                          }}
                        >
                          {item.icon}
                        </Box>
                        <Box
                          sx={{
                            paddingLeft: "10px",
                            fontSize: "13px",
                            width: "200px",
                          }}
                        >
                          {item.name}
                        </Box>
                      </Box>
                    </Grid>
                  ))}
              </Grid>
            </Box>
          </Grid>
          <Box sx={{ height: "700px", overflowY: "auto" }}>
            {stations.length > 0 ? (
              stations?.map((item) => (
                <Box
                  onClick={() => setNavigate(item)}
                  key={item.id}
                  className="flex items-center justify-between"
                  sx={{
                    padding: "20px 10px 20px 20px",
                    borderLeft: "5px solid transparent",
                    borderBottom: "1px solid var(--grey-4)",
                    backgroundColor: "var(--white)",
                    cursor: "pointer",
                    transition: "all .3s",
                    transitionProperty: "border-left-color, background-color",
                    "&:hover": {
                      borderLeft: "5px solid var(--primary)",
                      backgroundColor: "var(--grey-4)",
                      "& .arrow-forward-bg-color": {
                        fill: "var(--primary)",
                      },
                      "& .arrow-forward-fg-color": {
                        fill: "var(--white)",
                      },
                    },
                  }}
                >
                  <Box>
                    {geolocation?.latitude !== 0 &&
                      geolocation?.longitude !== 0 && (
                        <Box
                          sx={{
                            color: "var(--grey-2)",
                            fontSize: "12px",
                          }}
                        >
                          {calculateDistance(
                            item.latitude,
                            item.longitude,
                            geolocation?.latitude,
                            geolocation?.longitude
                          )}
                        </Box>
                      )}

                    <Box
                      sx={{
                        color: "var(--grey-1)",
                        fontWeight: "bold",
                        fontSize: "16px",
                        padding: "5px 0px",
                      }}
                    >
                      {item?.customFields?.raisonsociale}
                    </Box>
                    <Box
                      sx={{
                        color: "var(--grey-2)",
                        fontSize: "14px",
                        fontWeight: 400,
                      }}
                    >
                      <Typography variant="body2">
                        {item?.businessAddress?.line1}
                      </Typography>
                      <Typography variant="body2">
                        {item?.businessAddress?.city},{" "}
                        {item?.businessAddress?.postalCode}
                      </Typography>
                      <Typography variant="body2">
                        <a href={item?.businessPhone}>{item?.businessPhone}</a>
                      </Typography>
                    </Box>
                  </Box>
                  <Box>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width={25}
                      height={25}
                      viewBox="0 0 25 25"
                      className="svg-arrow-forward"
                      style={{ transition: "fill .3s" }}
                    >
                      <circle
                        className="arrow-forward-bg-color"
                        cx={12.5}
                        cy={12.5}
                        r={12.5}
                        fill="transparent"
                        style={{ transition: "fill .3s" }}
                      />
                      <path
                        className="arrow-forward-fg-color"
                        fill="var(--primary)"
                        d="M10.137 6.957l-.056.055a1 1 0 0 0 0 1.414l4.065 4.074-4.065 4.074a1 1 0 0 0 0 1.414l.056.055a1 1 0 0 0 1.414 0l5.543-5.543-5.543-5.543a1 1 0 0 0-1.414 0z"
                        style={{ transition: "fill .3s" }}
                      />
                    </svg>
                  </Box>
                </Box>
              ))
            ) : (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  marginTop: 20,
                  height: "60%",
                  flexDirection: "column",
                }}
              >
                <Place
                  style={{ fontSize: 70, color: "#ddd", marginBottom: 20 }}
                />
                <Typography variant="h6" sx={{ marginBottom: 2 }}>
                  Aucun magasin trouvé
                </Typography>
                <Typography variant="body2" sx={{ color: "#ddd" }}>
                  Étendez votre recherche en
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "#ddd", marginBottom: 3 }}
                >
                  désactivant certains filtres
                </Typography>
                <Button
                  sx={{
                    borderRadius: 5,
                    color: "var(--primary)",
                    borderColor: "var(--primary)",
                  }}
                  variant="outlined"
                  onClick={() => {
                    setActiveFilterOption([]);
                  }}
                >
                  Effacer les filtres
                </Button>
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
});

export default FilterAndMap;
